import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';

import SimpleDialog from '../common/NotificationDialog';

import { INVOICE, CREDIT_CARD } from '../../../containers/weployer/jobDetails/graphql/queries';

const mapPaymentMethods = {
  [INVOICE]: 'Invoice',
  [CREDIT_CARD]: 'Credit card',
  [null]: 'Not provided',
};

const StyledItemWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  height: 72px;
  padding-left: ${props => props.theme.spacing[2]};
  &:nth-child(2) {
    margin-left: ${props => props.theme.spacing[2]};
  }
`;

const StyledContent = styled.div`
  width: 552px;
`;

const StyledRow = styled.div`
  display: flex;
`;

const StyledItemTitle = styled.div`
  ${props => props.theme.typography.subtitle1};
`;

const StyledItemValue = styled.div`
  ${props => props.theme.typography.subtitle2};
  color: ${props => props.theme.colors.text.secondary};
`;

const Item = ({ title, value }) => (
  <StyledItemWrapper>
    <StyledItemTitle>{title}</StyledItemTitle>
    <StyledItemValue>{value}</StyledItemValue>
  </StyledItemWrapper>
);

Item.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};

const JobDetailsModal = ({ job, open, onClose }) => {
  const {
    street,
    state,
    postcode,
    city,
    createdAt,
    dressCode,
    experience,
    finishTime,
    isRemote,
    meetingInstructions,
    numberOfPositions,
    owner,
    paymentMethod,
    primarySkill,
    startTime,
    tasks,
    timeRequirements,
    title,
  } = job;

  return (
    <SimpleDialog
      open={open}
      onModalClose={onClose}
      title="Job Details"
    >
      <StyledContent>
        <Item
          title="Job title"
          value={title}
        />
        <StyledRow>
          <Item title="Created" value={moment(createdAt).format('DD MMM YYYY, h:mm a')} />
          <Item title="Owner" value={owner} />
        </StyledRow>
        <StyledRow>
          <Item title="Role" value={primarySkill} />
          <Item title="Positions" value={(numberOfPositions).toString()} />
        </StyledRow>
        <Item
          title="Office location"
          value={`${street}, ${city} ${state} ${postcode}`}
        />
        <Item
          title="Remote"
          value={isRemote ? 'Yes' : 'No'}
        />
        <Item
          title="Dates"
          value={`${moment(startTime).format('MMMM Do YYYY, hh:mm a')} - ${moment(finishTime).format('MMMM Do YYYY, hh:mm a')}`}
        />
        <Item
          title="Time Requirements"
          value={timeRequirements || 'None'}
        />
        <Item
          title="Tasks"
          value={tasks.join(', ')}
        />
        <Item
          title="Experience and Systems Knowledge"
          value={experience || 'None'}
        />
        <Item
          title="Meeting Instructions"
          value={meetingInstructions || 'None'}
        />
        <Item
          title="Dress Code"
          value={dressCode || 'None'}
        />
        <Item
          title="Payment Method"
          value={mapPaymentMethods[paymentMethod]}
        />
      </StyledContent>
    </SimpleDialog>
  );
};

JobDetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  job: PropTypes.shape({
    city: PropTypes.string,
    dressCode: PropTypes.string,
    startTime: PropTypes.string,
    finishTime: PropTypes.string,
    isRemote: PropTypes.bool,
    createdAt: PropTypes.string,
    experience: PropTypes.string,
    meetingInstructions: PropTypes.string,
    numberOfPositions: PropTypes.number,
    owner: PropTypes.string,
    paymentMethod: PropTypes.string,
    postcode: PropTypes.string,
    primarySkill: PropTypes.string,
    state: PropTypes.string,
    street: PropTypes.string,
    tasks: PropTypes.arrayOf(PropTypes.string),
    timeRequirements: PropTypes.string,
    title: PropTypes.string.isRequired,
  }).isRequired,
};

export default JobDetailsModal;
